import dayjs, { Dayjs } from "dayjs";

import { useUser } from "contexts/userContext/userContext";
import { useContent } from "contexts/contentContext/contentContext";
import { useEffect, useState } from "react";

import type { BillingDetailsT, UserDataT } from "models/models";

type ExpirationDataT = {
  expirationDate: string | null;
  daysLeft: number | 0;
  currDayIsExpirationDay?: boolean;
  gracePeriod: { daysLeft: number; expirationDate: string | null } | null;
};

const GRACE_PERIOD_LENGTH = 14;

const useSubscriptionExpiration = () => {
  const { getTextByKey } = useContent();

  const [expirationData, setExpirationData] = useState<ExpirationDataT>({
    expirationDate: null,
    daysLeft: 0,
    gracePeriod: null,
  });

  const { getUserData, getUserBillingDetails } = useUser();
  const user = getUserData() as UserDataT;
  const billingDetails = getUserBillingDetails() as BillingDetailsT;
  const isAutoRenew = billingDetails?.auto_renew;

  // User won't be counted as not active between charge and next payment date
  const autoRenewAdditionalDaysAmount = isAutoRenew ? 1 : 0;

  const expirationDate = user.Subscribtion_Expired
    ? dayjs(user.Subscribtion_Expired).subtract(
        autoRenewAdditionalDaysAmount,
        "day"
      )
    : null;
  const currentDate = dayjs();

  const daysUntilExpirationLeft = expirationDate
    ? expirationDate?.startOf("day")?.diff(currentDate.startOf("day"), "day")
    : null;
  const currDayIsExpirationDay = expirationDate?.isSame(currentDate, "day");

  const isExpired = (daysUntilExpirationLeft || 0) <= 0;

  // Get expiration date with translated month name
  const _getTranslatedExpirationDate = (date: Dayjs | null | undefined) => {
    if (!date) return null;

    // Get month name like "January"
    const expirationMonth = date.format("MMMM");

    // Get formatted date like "01, 2022"
    const expirationDateFormatted = date.format("DD, YYYY");

    // Translate month name
    const expirationDateTranslated =
      getTextByKey(expirationMonth.toLowerCase()) +
      // and concat with formatted date like "January 01, 2022"
      " " +
      expirationDateFormatted;

    return expirationDateTranslated;
  };

  const translatedExpirationDate = _getTranslatedExpirationDate(expirationDate);

  useEffect(() => {
    setExpirationData({
      expirationDate: translatedExpirationDate,
      daysLeft: daysUntilExpirationLeft
        ? Math.max(0, daysUntilExpirationLeft)
        : 0,
      currDayIsExpirationDay,
      gracePeriod: isExpired
        ? {
            daysLeft: Math.max(
              0,
              GRACE_PERIOD_LENGTH - Math.abs(daysUntilExpirationLeft as number)
            ),
            expirationDate: _getTranslatedExpirationDate(
              expirationDate?.add(GRACE_PERIOD_LENGTH, "day")
            ),
          }
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    translatedExpirationDate,
    daysUntilExpirationLeft,
    currDayIsExpirationDay,
    isExpired,
  ]);

  return expirationData;
};

export default useSubscriptionExpiration;
